import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { TrendingUp, TrendingDown, Wallet, Users, Tags } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const formatCurrency = (val) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val);

const CHART_MARGIN = { top: 20, right: 30, left: 20, bottom: 5 };
const BAR_RADIUS = [2, 2, 0, 0];
const TOOLTIP_STYLE = { borderRadius: '4px', border: '1px solid #E5E5E3', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' };
const LEGEND_STYLE = { fontSize: '12px' };

function AccountReportCard({ accounts, transactions, reportAccountId, setReportAccountId }) {
  const accountReport = useMemo(() => {
    if (reportAccountId === "none") return null;
    let income = 0;
    let expense = 0;
    let debt_payment = 0;
    let count = 0;
    transactions.forEach(tx => {
      if (tx.currency === "TL" && tx.account_id === reportAccountId) {
        if (tx.type === "income") income += tx.amount;
        else if (tx.type === "expense") expense += tx.amount;
        else if (tx.type === "debt_payment") debt_payment += tx.amount;
        count++;
      }
    });
    const accName = accounts.find(a => a.id === reportAccountId)?.name || "Bilinmiyor";
    return { name: accName, income, expense, balance: income - expense - debt_payment, debt_payment, count };
  }, [reportAccountId, transactions, accounts]);

  return (
    <Card className="swiss-card border-l-4 border-l-primary">
      <CardHeader className="pb-4 border-b border-border bg-neutral-50/50">
        <div className="flex flex-col space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center">
              <Users className="w-4 h-4 mr-2" /> Cari Hesap Özeti
            </CardTitle>
          </div>
          <Select value={reportAccountId} onValueChange={setReportAccountId}>
            <SelectTrigger className="h-10 bg-white" data-testid="report-account-select">
              <SelectValue placeholder="Raporlanacak Cari Hesap Seçin..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seçim Yapılmadı --</SelectItem>
              {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {accountReport ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Toplam İşlem:</span>
              <span className="font-bold">{accountReport.count} adet</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Hesaptan Gelen (Tahsilat):</span>
              <span className="font-bold text-income">{formatCurrency(accountReport.income)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Hesaba Çıkan (Ödeme):</span>
              <span className="font-bold text-expense">{formatCurrency(accountReport.expense)}</span>
            </div>
            {accountReport.debt_payment > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Bu Hesaba Borç Ödeme:</span>
                <span className="font-bold text-blue-600">{formatCurrency(accountReport.debt_payment)}</span>
              </div>
            )}
            <div className="pt-3 mt-3 border-t border-dashed flex justify-between items-center">
              <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Net Durum:</span>
              <span className={`text-xl font-heading font-bold ${accountReport.balance >= 0 ? 'text-income' : 'text-expense'}`}>
                {accountReport.balance > 0 ? '+' : ''}{formatCurrency(accountReport.balance)}
              </span>
            </div>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
            Raporu görmek için yukarıdan bir cari hesap seçin.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TagReportCard({ tags, transactions, reportTagId, setReportTagId }) {
  const tagReport = useMemo(() => {
    if (reportTagId === "none") return null;
    let income = 0;
    let expense = 0;
    let debt_payment = 0;
    let count = 0;
    transactions.forEach(tx => {
      if (tx.currency === "TL" && tx.tags.includes(reportTagId)) {
        if (tx.type === "income") income += tx.amount;
        else if (tx.type === "expense") expense += tx.amount;
        else if (tx.type === "debt_payment") debt_payment += tx.amount;
        count++;
      }
    });
    const tagName = tags.find(t => t.id === reportTagId)?.name || "Bilinmiyor";
    return { name: tagName, income, expense, balance: income - expense - debt_payment, debt_payment, count };
  }, [reportTagId, transactions, tags]);

  return (
    <Card className="swiss-card border-l-4 border-l-primary">
      <CardHeader className="pb-4 border-b border-border bg-neutral-50/50">
        <div className="flex flex-col space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center">
              <Tags className="w-4 h-4 mr-2" /> Etiket Özeti
            </CardTitle>
          </div>
          <Select value={reportTagId} onValueChange={setReportTagId}>
            <SelectTrigger className="h-10 bg-white" data-testid="report-tag-select">
              <SelectValue placeholder="Raporlanacak Etiket Seçin..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seçim Yapılmadı --</SelectItem>
              {tags.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {tagReport ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Toplam İşlem:</span>
              <span className="font-bold">{tagReport.count} adet</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Etiketle Gelen:</span>
              <span className="font-bold text-income">{formatCurrency(tagReport.income)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Bu Etiketle Çıkan (Harcama):</span>
              <span className="font-bold text-expense">{formatCurrency(tagReport.expense)}</span>
            </div>
            {tagReport.debt_payment > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Bu Etiketle Borç Ödeme:</span>
                <span className="font-bold text-blue-600">{formatCurrency(tagReport.debt_payment)}</span>
              </div>
            )}
            <div className="pt-3 mt-3 border-t border-dashed flex justify-between items-center">
              <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Net Etki:</span>
              <span className={`text-xl font-heading font-bold ${tagReport.balance >= 0 ? 'text-income' : 'text-expense'}`}>
                {tagReport.balance > 0 ? '+' : ''}{formatCurrency(tagReport.balance)}
              </span>
            </div>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
            Raporu görmek için yukarıdan bir etiket seçin.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardStats({ tlSummary }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Kasa (Bakiye)</CardTitle>
          <Wallet className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading" data-testid="total-balance-value">
            {formatCurrency(tlSummary.balance)}
          </div>
        </CardContent>
      </Card>

      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Toplam Gelir</CardTitle>
          <TrendingUp className="h-4 w-4 text-income" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading text-income" data-testid="total-income-value">
            {formatCurrency(tlSummary.income)}
          </div>
        </CardContent>
      </Card>

      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Toplam Gider</CardTitle>
          <TrendingDown className="h-4 w-4 text-expense" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading text-expense" data-testid="total-expense-value">
            {formatCurrency(tlSummary.expense)}
          </div>
        </CardContent>
      </Card>

      <Card className="swiss-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">Toplam Borç Ödeme</CardTitle>
          <TrendingDown className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold font-heading text-blue-600" data-testid="total-debt-value">
            {formatCurrency(tlSummary.debt_payment || 0)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MonthlyCashFlowChart({ transactions }) {
  const monthlyData = useMemo(() => {
    const monthlyMap = {};
    const tlTxs = transactions.filter(t => t.currency === "TL");
    tlTxs.forEach(tx => {
      const date = new Date(tx.date);
      const monthStr = date.toLocaleString('tr-TR', { month: 'short', year: 'numeric' });
      if (!monthlyMap[monthStr]) monthlyMap[monthStr] = { month: monthStr, income: 0, expense: 0, debt_payment: 0, sortKey: date.getTime() };
      
      if (tx.type === "income") monthlyMap[monthStr].income += tx.amount;
      else if (tx.type === "expense") monthlyMap[monthStr].expense += tx.amount;
      else if (tx.type === "debt_payment") monthlyMap[monthStr].debt_payment += tx.amount;
    });
    
    return Object.values(monthlyMap)
      .sort((a, b) => a.sortKey - b.sortKey)
      .slice(-6);
  }, [transactions]);

  return (
    <Card className="swiss-card">
      <CardHeader>
        <CardTitle className="text-lg font-heading font-medium">Aylık Nakit Akışı (TL)</CardTitle>
      </CardHeader>
      <CardContent className="h-[300px] w-full">
        {monthlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E3" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#5C5C5C' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#5C5C5C' }} tickFormatter={(value) => `₺${value}`} />
              <Tooltip cursor={{ fill: '#F7F7F5' }} contentStyle={TOOLTIP_STYLE} />
              <Legend iconType="circle" wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="income" name="Gelir" fill="#276339" radius={BAR_RADIUS} />
              <Bar dataKey="expense" name="Gider" fill="#A13426" radius={BAR_RADIUS} />
              <Bar dataKey="debt_payment" name="Borç Ödeme" fill="#2563eb" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Yeterli veri yok.</div>
        )}
      </CardContent>
    </Card>
  );
}

function TagExpensePieChart({ transactions, tags }) {
  const tagData = useMemo(() => {
    const tagMap = {};
    const tlExpenses = transactions.filter(t => t.currency === "TL" && (t.type === "expense" || t.type === "debt_payment"));
    
    tlExpenses.forEach(tx => {
      if (!tx.tags || tx.tags.length === 0) {
        if (!tagMap["Diğer"]) tagMap["Diğer"] = { name: "Diğer", value: 0, color: "#8F8F8F" };
        tagMap["Diğer"].value += tx.amount;
      } else {
        tx.tags.forEach(tagId => {
          const tagObj = tags.find(t => t.id === tagId);
          const tagName = tagObj ? tagObj.name : "Bilinmeyen";
          const tagColor = tagObj ? tagObj.color : "#8F8F8F";
          
          if (!tagMap[tagName]) tagMap[tagName] = { name: tagName, value: 0, color: tagColor };
          tagMap[tagName].value += tx.amount;
        });
      }
    });

    return Object.values(tagMap).sort((a, b) => b.value - a.value);
  }, [transactions, tags]);

  return (
    <Card className="swiss-card">
      <CardHeader>
        <CardTitle className="text-lg font-heading font-medium">Genel Etiket Harcamaları (TL)</CardTitle>
      </CardHeader>
      <CardContent className="h-[300px] w-full">
        {tagData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={tagData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {tagData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => `₺${value}`} />
              <Legend iconType="circle" layout="vertical" verticalAlign="middle" align="right" wrapperStyle={LEGEND_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Yeterli harcama verisi yok.</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [tags, setTags] = useState([]);
  const [accounts, setAccounts] = useState([]);
  
  const [reportAccountId, setReportAccountId] = useState("none");
  const [reportTagId, setReportTagId] = useState("none");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, txRes, tagsRes, accRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/transactions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tags`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true })
      ]);
      setSummary(dashRes.data);
      setTransactions(txRes.data);
      setTags(tagsRes.data);
      setAccounts(accRes.data);
    } catch (err) {
      toast.error("Dashboard verileri yüklenemedi.");
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const tlSummary = summary["TL"] || { income: 0, expense: 0, debt_payment: 0, balance: 0 };
  
  const otherCurrencies = useMemo(() => {
    return Object.keys(summary).filter(k => k !== "TL");
  }, [summary]);

  return (
    <div className="space-y-8 pb-10">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <h1 className="text-3xl font-heading font-bold">Özet (TL)</h1>
      </div>

      <DashboardStats tlSummary={tlSummary} />
      
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-heading font-bold tracking-tight border-b border-border pb-2">Gelişmiş Raporlar (TL)</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AccountReportCard accounts={accounts} transactions={transactions} reportAccountId={reportAccountId} setReportAccountId={setReportAccountId} />
          <TagReportCard tags={tags} transactions={transactions} reportTagId={reportTagId} setReportTagId={setReportTagId} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <MonthlyCashFlowChart transactions={transactions} />
        <TagExpensePieChart transactions={transactions} tags={tags} />
      </div>

      {otherCurrencies.length > 0 && (
        <div className="mt-8 space-y-4">
          <h2 className="text-xl font-heading font-medium border-b border-border pb-2">Diğer Para Birimleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {otherCurrencies.map(curr => (
              <Card key={curr} className="swiss-card border-l-4 border-l-primary">
                <CardHeader className="pb-2 bg-neutral-50/50 border-b border-border">
                  <CardTitle className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">{curr} Bakiyesi</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold font-heading">
                    {summary[curr].balance.toLocaleString()} {curr}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
